import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React, { CSSProperties, FC, ReactNode, useEffect, useRef, useState } from "react";
import { ContextMenuDataItem } from "../../../ComponentInterface";
import ContextMenu from "../../NotWorkArea(Side&Head)/ContextMenu";
import { PinnedIcon, UnPinnedIcon } from "./PinIcons";
import clsx from "clsx";

interface SmetaTabsProps {
  children: ReactNode;
  // Вкладки снизу, сверху или скрыты
  position?: "top" | "bottom" | "hidden";
  // Отделить вкладки от соседних блоков тонкой линией
  border?: boolean;
  //Вкладки можно закреплять
  onTabPin?: (pinned: boolean, index: number, data?: unknown) => void | Promise<void>;
  //Вкладки можно закрывать
  onTabClose?: (
    indices: { oldIndex: number; newIndex: number },
    data: { oldData: unknown; newData: unknown },
  ) => void | Promise<void>;
  //Повторный клик на таб
  onTabReClick?: (index: number, title?: string) => void | Promise<void>;
  id?: string;
  //Изменение индекса вкладки
  onTabIndexChange?: (
    index: number,
    tabElement: HTMLElement,
    data?: unknown,
    actualIndex?: number,
  ) => void | Promise<void>;
  //Контролирование выбранной вкладки
  tabIndex?: number;
  style?: React.CSSProperties;
  className?: string;
  class?: string;
  animation?: string;
  additionalContextMenuData?: ContextMenuDataItem[];
  additionalOnMenuItemClick?: (data: ContextMenuDataItem) => void;
}

export interface SmetaTabItemProps {
  children: ReactNode;
  style?: CSSProperties;
  pinned?: boolean;
  label: string;
  closable?: boolean;
  hidden?: boolean;
  data?: unknown;
  title?: string;
  id?: string;
}

interface SmetaTabsElement extends HTMLDivElement {
  getSelectedContent?: () => HTMLDivElement | null | undefined;
}

type SmetaTabElement = React.ReactElement<SmetaTabItemProps>;

const getTabKey = (child: SmetaTabElement, index: number): React.Key =>
  child.props.id ?? child.key ?? `smeta-tab-${index}`;

// eslint-disable-next-line @typescript-eslint/naming-convention
const SmetaTabs: FC<SmetaTabsProps> = ({
  children,
  position = "top",
  border = false,
  onTabPin,
  onTabClose,
  onTabIndexChange,
  onTabReClick,
  tabIndex,
  id,
  style,
  className,
  class: classProp,
  additionalContextMenuData,
  additionalOnMenuItemClick,
}) => {
  const reactChildren = React.Children.toArray(children).filter(
    (el): el is SmetaTabElement => React.isValidElement<SmetaTabItemProps>(el) && !el.props.hidden,
  );
  const initialTabIndex = tabIndex ?? 0;
  const [activeTab, setActiveTab] = useState(tabIndex ?? 0);
  const [visitedTabs, setVisitedTabs] = useState<Set<React.Key>>(() => {
    const initialTab = reactChildren[initialTabIndex];
    return new Set(initialTab ? [getTabKey(initialTab, initialTabIndex)] : []);
  }); //сохранение посещенных вкладок
  const tabsRef = useRef<SmetaTabsElement | null>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]); //Для передачи ссылок на вкладку при изменении индекса(в основном нужно для грида, т.к. он на чистом js)

  const updateActiveTab = (index: number) => {
    // Обновляем активную вкладку и разрешаем рендер ее контента
    setActiveTab(index);
    const activeChild = reactChildren[index];
    if (activeChild) {
      const activeChildKey = getTabKey(activeChild, index);
      setVisitedTabs((prev) => (prev.has(activeChildKey) ? prev : new Set(prev).add(activeChildKey)));
    }
  };

  const selectedIndex = tabIndex ?? activeTab;
  const selectedChild = reactChildren[selectedIndex];
  const selectedTabKey = selectedChild ? getTabKey(selectedChild, selectedIndex) : undefined;

  useEffect(() => {
    // Дети могут появиться позже, поэтому следим не только за индексом, но и за ключом вкладки
    if (tabIndex !== undefined) updateActiveTab(tabIndex || 0);
    else if (selectedTabKey !== undefined) updateActiveTab(activeTab);
    if (tabsRef.current) tabsRef.current.getSelectedContent = () => tabRefs.current[tabIndex ?? activeTab];
  }, [selectedTabKey, tabIndex]);

  const handleTabChange = async (index: number, actualIndex: number, propsData?: unknown) => {
    //функция для смены вкладки
    updateActiveTab(index); //todo обновление по актуальному индексу
    if (tabRefs.current[index]) {
      setTimeout(async () => await onTabIndexChange?.(index, tabRefs.current[index]!, propsData, actualIndex), 0);
    }
  };

  const getNewTabIndexAfterClose = (index: number) => {
    if (activeTab === index) {
      return Math.max(0, activeTab - 1);
    }
    return activeTab;
  };

  const handleTabClose = async (index: number, data?: unknown) => {
    // Обновление активной вкладки на последний доступный индекс
    const newIndex = getNewTabIndexAfterClose(index);
    const newTabData = reactChildren[newIndex].props.data;
    updateActiveTab(newIndex);
    if (onTabClose) {
      await onTabClose({ oldIndex: index, newIndex }, { oldData: data, newData: newTabData });
    }
  };

  const renderTabs = () => {
    //рендер вкладок
    let contextMenuData: ContextMenuDataItem[] = reactChildren.map((child, index) => ({
      //формирование данных для контекстного меню
      Caption: child.props.label,
      value: "select",
      index,
      Image: activeTab === index ? <CheckIcon /> : undefined,
    }));
    if (additionalContextMenuData) {
      contextMenuData.push({ Caption: "Divider" });
      contextMenuData = contextMenuData.concat(additionalContextMenuData);
    }
    const isBottomPosition = position === "bottom";
    const tabsBorder = border
      ? {
          [isBottomPosition ? "borderTop" : "borderBottom"]: "1px solid #d9dee8",
        }
      : {};

    return (
      <ContextMenu
        style={{ height: `${position === "hidden" ? 0 : 40}px`, flex: `0 0 ${position === "hidden" ? 0 : 40}px` }}
        onMenuItemClick={(data: ContextMenuDataItem) => {
          if (data.value === "select") {
            const index = data.index!;
            handleTabChange(index, index, reactChildren[index].props.data);
          } else additionalOnMenuItemClick?.(data);
        }}
        data={contextMenuData as { Caption: string; value: string; index: number }[]}
      >
        <Tabs
          value={activeTab}
          onChange={(_event: React.SyntheticEvent, newValue: number) => {
            const childProps = reactChildren[newValue].props;
            handleTabChange(newValue, newValue, childProps.data);
          }}
          aria-label="Smeta Tabs"
          variant="scrollable"
          scrollButtons={false}
          slotProps={{ indicator: { style: { backgroundColor: "#0b6fd3", height: 2 } } }}
          sx={{
            minHeight: "40px",
            height: "40px",
            display: position === "hidden" ? "none" : "flex",
            backgroundColor: "#fff",
            ...tabsBorder,
            "& .MuiTabs-scrollButtons": {
              color: "#667085",
              width: "32px",
            },
            "& .MuiTab-root": {
              minHeight: "40px",
              height: "40px",
              minWidth: "auto",
              padding: "0 20px",
              alignItems: "center",
              color: "#667085",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              textTransform: "none",
              transition: "color 0.2s ease, background-color 0.2s ease",
              "&:not(.Mui-selected):hover": {
                backgroundColor: "#f7f9fc",
                color: "#175cd3",
              },
              "&.Mui-selected": {
                color: "#0b6fd3",
                fontWeight: 700,
              },
              "& .MuiTab-iconWrapper": {
                margin: 0,
              },
              "& .MuiIconButton-root": {
                padding: "2px",
                margin: "0 2px",
              },
            },
          }}
        >
          {reactChildren.map((child, index) => {
            const childProps = child.props; //получение пропсов вкладки
            const tabKey = getTabKey(child, index);
            const canPin = childProps.pinned !== undefined;
            if (childProps.hidden) {
              return null;
            }
            return (
              <Tab
                title={childProps.title || childProps.label || ""}
                key={tabKey}
                onClick={() => {
                  if (activeTab === index && onTabReClick) onTabReClick(index, childProps.title || childProps.label);
                }}
                label={
                  <>
                    {canPin && (
                      <IconButton onClick={() => onTabPin?.(!!childProps.pinned, index, childProps.data)} size="small">
                        {childProps.pinned ? <PinnedIcon /> : <UnPinnedIcon />}
                      </IconButton>
                    )}
                    {childProps.label}
                    {childProps.closable && (
                      <IconButton onClick={() => handleTabClose(index, childProps.data)} size="small">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </>
                }
                iconPosition="end"
              />
            );
          })}
        </Tabs>
      </ContextMenu>
    );
  };

  const renderContent = () => {
    //рендерим контент с проверкой: посещали ли вкладку ранее
    return reactChildren.map((child, index) => {
      const tabKey = getTabKey(child, index);
      if (child.props.hidden) {
        return null;
      }
      return (
        <div
          id={child.props.id}
          key={tabKey}
          className={clsx("SmetaTab", activeTab === index && "selected")}
          ref={(el) => {
            tabRefs.current[index] = el;
          }}
          style={{
            display: activeTab === index ? "block" : "none",
            boxSizing: "border-box",
            height: "100%",
            padding: 0,
            position: "relative",
          }}
        >
          {visitedTabs.has(tabKey) && child.props.children}
        </div>
      );
    });
  };

  return (
    <Box
      ref={tabsRef}
      selected-index={tabIndex}
      id={id || ""}
      className={clsx("smeta-tabs", className, classProp)}
      style={{
        display: "flex",
        flexDirection: position === "top" ? "column" : "column-reverse",
        width: "100%",
        minHeight: position === "hidden" ? 0 : 40,
        ...style,
      }}
    >
      {renderTabs()}
      <div style={{ flex: "1 1 0", minWidth: 0, minHeight: 0, overflow: "hidden", position: "relative" }}>
        {renderContent()}
      </div>
    </Box>
  );
};

export default SmetaTabs;
